import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {HugeiconsIcon} from '@hugeicons/react';
import {Cancel01Icon, CheckIcon, Copy01Icon, KeyboardIcon} from '@hugeicons/core-free-icons';
import {grey} from '@mui/material/colors';
import {VocabularyRepository} from '../../../../repositories/VocabularyRepository';
import {toast} from 'react-toastify';

const PHONEMIZER_CHARS = [
  // Vowels
  {symbol: 'a',  ipa: 'a',   description: 'open front unrounded — father'},
  {symbol: 'e',  ipa: 'e',   description: 'close-mid front — bed'},
  {symbol: 'i',  ipa: 'iː',  description: 'close front — see'},
  {symbol: 'o',  ipa: 'o',   description: 'close-mid back — go'},
  {symbol: 'u',  ipa: 'uː',  description: 'close back — food'},
  {symbol: '@',  ipa: 'ə',   description: 'schwa — about'},
  {symbol: '{',  ipa: 'æ',   description: 'near-open front — cat'},
  {symbol: 'V',  ipa: 'ʌ',   description: 'open-mid back — cup'},
  {symbol: 'A',  ipa: 'ɑː',  description: 'open back — father (long)'},
  {symbol: 'E',  ipa: 'ɛ',   description: 'open-mid front — dress'},
  {symbol: 'I',  ipa: 'ɪ',   description: 'near-close near-front — kit'},
  {symbol: 'O',  ipa: 'ɔ',   description: 'open-mid back rounded — lot'},
  {symbol: 'U',  ipa: 'ʊ',   description: 'near-close near-back — foot'},
  {symbol: '3',  ipa: 'ɜː',  description: 'open-mid central — nurse'},
  {symbol: 'y',  ipa: 'y',   description: 'close front rounded — French tu'},
  {symbol: '2',  ipa: 'ø',   description: 'close-mid front rounded — French feu'},
  // Consonants
  {symbol: 'p',  ipa: 'p',   description: 'voiceless bilabial stop — pen'},
  {symbol: 'b',  ipa: 'b',   description: 'voiced bilabial stop — boy'},
  {symbol: 't',  ipa: 't',   description: 'voiceless alveolar stop — top'},
  {symbol: 'd',  ipa: 'd',   description: 'voiced alveolar stop — dog'},
  {symbol: 'k',  ipa: 'k',   description: 'voiceless velar stop — king'},
  {symbol: 'g',  ipa: 'g',   description: 'voiced velar stop — go'},
  {symbol: 'f',  ipa: 'f',   description: 'voiceless labiodental — fish'},
  {symbol: 'v',  ipa: 'v',   description: 'voiced labiodental — van'},
  {symbol: 'T',  ipa: 'θ',   description: 'voiceless dental — think'},
  {symbol: 'D',  ipa: 'ð',   description: 'voiced dental — this'},
  {symbol: 's',  ipa: 's',   description: 'voiceless alveolar fricative — sun'},
  {symbol: 'z',  ipa: 'z',   description: 'voiced alveolar fricative — zoo'},
  {symbol: 'S',  ipa: 'ʃ',   description: 'voiceless postalveolar — shoe'},
  {symbol: 'Z',  ipa: 'ʒ',   description: 'voiced postalveolar — measure'},
  {symbol: 'h',  ipa: 'h',   description: 'glottal fricative — hat'},
  {symbol: 'x',  ipa: 'x',   description: 'velar fricative — loch'},
  {symbol: 'tS', ipa: 'tʃ',  description: 'voiceless postalveolar affricate — church'},
  {symbol: 'dZ', ipa: 'dʒ',  description: 'voiced postalveolar affricate — judge'},
  {symbol: 'ts', ipa: 'ts',  description: 'voiceless alveolar affricate — cats'},
  {symbol: 'dz', ipa: 'dz',  description: 'voiced alveolar affricate'},
  {symbol: 'tC', ipa: 'tɕ',  description: 'voiceless palatal affricate — soft ch'},
  {symbol: 'dJ', ipa: 'dʑ',  description: 'voiced palatal affricate — soft j'},
  {symbol: 'm',  ipa: 'm',   description: 'bilabial nasal — man'},
  {symbol: 'n',  ipa: 'n',   description: 'alveolar nasal — no'},
  {symbol: 'N',  ipa: 'ŋ',   description: 'velar nasal — sing'},
  {symbol: 'J',  ipa: 'ɲ',   description: 'palatal nasal — canyon'},
  {symbol: 'l',  ipa: 'l',   description: 'alveolar lateral — let'},
  {symbol: 'L',  ipa: 'ʎ',   description: 'palatal lateral — million'},
  {symbol: 'r',  ipa: 'r',   description: 'alveolar trill — rolled r'},
  {symbol: 'R',  ipa: 'ʁ',   description: 'uvular fricative — French r'},
  {symbol: 'j',  ipa: 'j',   description: 'palatal approximant — yes'},
  {symbol: 'w',  ipa: 'w',   description: 'labial-velar approximant — wet'},
  {symbol: '?',  ipa: 'ʔ',   description: 'glottal stop'},
];

const REF_COLUMNS = [
  {key: 'symbol',      label: 'eSpeak-ng'},
  {key: 'ipa',         label: 'IPA'},
  {key: 'description', label: 'Description'},
];

export const PhonemizeEditModal = ({open, onClose, onSuccess, project, item, vocabularyType}) => {
  const [phrase, setPhrase] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [saving, setSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [copiedCell, setCopiedCell] = useState(null);

  useEffect(() => {
    if (open && item) {
      setPhrase(item.phrase || '');
      setPronunciation(item.pronunciation || '');
    }
  }, [open, item]);

  const handleTogglePopover = (e) => setAnchorEl(prev => prev ? null : e.currentTarget);
  const handleClosePopover = () => setAnchorEl(null);

  const handleCopy = (text, cellKey) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedCell(cellKey);
    setTimeout(() => setCopiedCell(null), 1500);
  };

  const handleSave = async () => {
    if (!phrase.trim() || !pronunciation.trim()) {
      toast.error("Both phrase and pronunciation are required");
      return;
    }
    setSaving(true);
    try {
      await VocabularyRepository.update(item.id, {
        id: item.id,
        phrase: phrase.trim(),
        pronunciation: pronunciation.trim(),
        type: vocabularyType,
        flowUid: project?.id,
      });
      toast.success("Phonemizer entry updated successfully");
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update phonemizer entry");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setPhrase('');
    setPronunciation('');
    setAnchorEl(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{pb: 1}}>
          <Typography variant="h6" fontWeight={600}>Edit Phonemizer Entry</Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Stack gap={2.5} sx={{pt: 1}}>
            <Box>
              <Typography variant="caption" color={grey[600]} sx={{mb: 1, display: 'block'}}>
                <strong>Phrase</strong> — the exact word or text the TTS will encounter in the input.
              </Typography>
              <TextField
                placeholder="e.g. VIP"
                value={phrase}
                onChange={e => setPhrase(e.target.value)}
                fullWidth
                InputLabelProps={{shrink: true}}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
              />
            </Box>

            <Box>
              <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{mb: 1}}>
                <Typography variant="caption" color={grey[600]} sx={{flex: 1}}>
                  <strong>Pronunciation</strong> — the eSpeak-ng phoneme string the TTS will use to
                  produce audio (e.g. "v I p" for VIP). Use X-SAMPA-based eSpeak-ng symbols; click{' '}
                  <HugeiconsIcon icon={KeyboardIcon} size={11} style={{verticalAlign: 'middle'}}/>{' '}
                  to see the full phoneme reference table.
                </Typography>
                <Tooltip title="Phoneme character reference" arrow>
                  <IconButton
                    size="small"
                    onClick={handleTogglePopover}
                    sx={{flexShrink: 0, mt: -0.25, color: anchorEl ? 'primary.main' : 'inherit'}}
                  >
                    <HugeiconsIcon icon={KeyboardIcon} size={18}/>
                  </IconButton>
                </Tooltip>
              </Stack>
              <TextField
                placeholder="e.g. v I p"
                value={pronunciation}
                onChange={e => setPronunciation(e.target.value)}
                fullWidth
                InputLabelProps={{shrink: true}}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={handleClose} variant="ghost">Cancel</Button>
          <Button
            onClick={handleSave}
            variant="modal"
            disabled={saving || !phrase.trim() || !pronunciation.trim()}
          >
            {saving ? 'Saving…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{sx: {borderRadius: 2, minWidth: 500}}}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between"
               sx={{px: 2, pt: 1.5, pb: 0.5, borderBottom: '1px solid', borderColor: 'divider'}}>
          <Typography variant="subtitle2" fontWeight={600}>Phoneme character reference</Typography>
          <IconButton size="small" onClick={handleClosePopover}>
            <HugeiconsIcon icon={Cancel01Icon} size={16}/>
          </IconButton>
        </Stack>
        <Box sx={{maxHeight: 300, overflow: 'auto'}}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {REF_COLUMNS.map(col => (
                  <TableCell key={col.key} sx={{fontWeight: 700, fontSize: '0.75rem', bgcolor: 'background.paper'}}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PHONEMIZER_CHARS.map((row, idx) => (
                <TableRow key={row.letter} hover>
                  {REF_COLUMNS.map(col => {
                    const cellKey = `${idx}-${col.key}`;
                    const isCopied = copiedCell === cellKey;
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          fontSize: '0.8rem', position: 'relative', pr: 1,
                          '&:hover .copy-btn': {opacity: 1},
                        }}
                      >
                        {row[col.key]}
                        <IconButton
                          size="small"
                          className="copy-btn"
                          onClick={() => handleCopy(row[col.key], cellKey)}
                          sx={{
                            opacity: 0,
                            position: 'absolute', right: 2, top: '50%',
                            transform: 'translateY(-50%)', p: 0.25,
                            transition: 'opacity 0.15s',
                          }}
                        >
                          {isCopied ? <HugeiconsIcon icon={CheckIcon} size={13} color="green"/> : <HugeiconsIcon icon={Copy01Icon} size={13}/>} 
                        </IconButton>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Popover>
    </>
  );
};
